import React, { useState } from "react";
import { ReactComponent as MetaMaskIcon } from "../../assets/img/common/metamask-icon.svg";
import CloseIcon from "../../assets/img/common/close-gray-icon.jpg";
import { useRef } from "react";
import useOutsideClick from "../../hooks/useOutsideClick";
import notify from "../common/notify";

/**
 * Discover all EIP-6963 providers (multi-wallet standard).
 * Returns an array of { info, provider } objects announced by extensions.
 */
async function discoverEIP6963Providers(timeoutMs = 300) {
  if (typeof window === "undefined") return [];
  const found = [];
  const handler = (event) => {
    if (event?.detail?.provider) found.push(event.detail);
  };
  window.addEventListener("eip6963:announceProvider", handler);
  try {
    window.dispatchEvent(new Event("eip6963:requestProvider"));
    await new Promise((resolve) => setTimeout(resolve, timeoutMs));
  } finally {
    window.removeEventListener("eip6963:announceProvider", handler);
  }
  return found;
}

/**
 * Return the MetaMask provider specifically, ignoring other injected wallets
 * (e.g. OKX Wallet) that may have overridden window.ethereum.
 *
 * Priority:
 *  1. EIP-6963 provider with RDNS "io.metamask"
 *  2. EIP-6963 provider with isMetaMask flag
 *  3. window.ethereum.providers array (legacy multi-wallet injection)
 *  4. window.ethereum only when it is genuinely MetaMask
 */
async function getMetaMaskProvider() {
  if (typeof window === "undefined") return null;

  // 1 & 2: EIP-6963 (preferred – wallets don't fight over window.ethereum)
  const details = await discoverEIP6963Providers();
  const byRdns = details.find((d) => d?.info?.rdns === "io.metamask");
  if (byRdns?.provider) return byRdns.provider;

  const byFlag = details.find((d) => d?.provider?.isMetaMask);
  if (byFlag?.provider) return byFlag.provider;

  // 3: Legacy array injected by some wallet managers
  const eth = window.ethereum;
  if (eth?.providers && Array.isArray(eth.providers)) {
    const mm = eth.providers.find((p) => p?.isMetaMask);
    return mm || null;
  }

  // 4: Single injected provider – only accept if it is truly MetaMask
  if (eth?.isMetaMask) return eth;

  return null;
}

const ConnectWalletModal = ({ show, setShow }) => {
  const modalRef = useRef(null);
  useOutsideClick(modalRef, () => setShow(false), null, show);
  const [publicAddress, setPublicAddress] = useState("");
  const handleConnectWallet = async () => {
    const metamask = await getMetaMaskProvider();

    if (!metamask) {
      notify("MetaMask not found. Please install MetaMask and try again.", "error");
      return;
    }

    metamask
      .request({ method: "eth_requestAccounts" })
      .then((accounts) => {
        const address = accounts[0];
        setPublicAddress(address);
        localStorage.setItem("public_address", address);
        notify("Connected", "success");
        window.location.reload();
      })
      .catch((error) => {
        if (error?.code === 4001) {
          notify("Connection rejected. Please approve the request in MetaMask.", "error");
        } else {
          notify("Something went wrong, please try again!", "error");
        }
      });
  };

  return (
    <div className="w-screen h-screen flex justify-center items-center bg-[#241b4795] backdrop-blur-md absolute top-0 left-0 roboto-font">
      <div
        ref={modalRef}
        className="w-11/12 md:w-[650px] flex flex-col items-center pb-10 pt-1 rounded-3xl bg-white"
      >
        <div className="w-full flex justify-end py-3 px-4">
          <button onClick={() => setShow(false)}>
            <img src={CloseIcon} alt="CloseIcon" />
            {/* <CloseIcon /> */}
          </button>
        </div>
        <span className="text-3xl font-semibold abel-font mt-1 ">
          Connect Wallet
        </span>
        <span className="text-[#464646] text-lg font-medium mt-4">
          Login with MetaMask
        </span>
        <button
          onClick={handleConnectWallet}
          className="w-10/12 md:w-8/12 h-[68px] flex items-center px-5 mt-4 rounded-2xl border border-[#9B9B9B] transition-all duration-200 hover:bg-[#5858586c] "
        >
          <span className="w-10 h-10 flex justify-center items-center rounded-full bg-[#9B9B9B] mr-3">
            <MetaMaskIcon />
          </span>
          <span className="text-lg font-medium ">Metamask</span>
        </button>
      </div>
    </div>
  );
};

export default ConnectWalletModal;
